import { useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Info,
  FileText,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react';
import { format, subMonths, startOfMonth, eachMonthOfInterval } from 'date-fns';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface InvoiceParticipationHeatmapProps {
  dateFilter: string;
  utilityTypeFilter: string;
  billTypeFilter: string;
  locationFilter: string;
  vendorFilter: string;
  accountStatusFilter: string;
}

interface MonthlyStatus {
  month: string;
  status: 'covered' | 'partial' | 'missing';
  hasInvoice: boolean;
  billCount: number;
}

interface AccountRow {
  location: string;
  account: string;
  meter: string;
  utility: string;
  billType: string;
  percentLast12Mo: number;
  months: MonthlyStatus[];
}

// Generate months for the last 13 months (Dec 2025 back to Dec 2024)
const generateMonths = () => {
  const endDate = new Date(2025, 11, 1); // December 2025
  const startDate = subMonths(endDate, 12); // December 2024
  return eachMonthOfInterval({ start: startDate, end: endDate }).reverse();
};

// Dummy data generator
const generateDummyData = (): AccountRow[] => {
  const locations = [
    '1 Ashgrove Court',
    '10 Koehler Ave Apt. #9',
    '10 South Terrace Dr Apt. #6',
    '11001 Quailwood Drive',
    '11034 Quailwood Drive',
    '11503 Springfield Pike',
    '14 Koehler Ave Apt. #7',
    '2183 Quailhollow Place',
    '40 South Terrace Dr Apt. #7',
    '3241 Sovereign Dr Apt. #7',
  ];

  const utilities = ['WATER', 'ELECTRIC', 'SEWER', 'NATURALGAS', 'REFUSE'];
  const billTypes = ['Full Service', 'Distribution Only', 'Supply Only'];
  const months = generateMonths();

  return locations.flatMap((location, locIdx) => {
    return utilities.flatMap((utility, utilIdx) => {
      return billTypes.map((billType, billIdx) => {
        const account = `${15000 + locIdx * 100 + utilIdx * 10 + billIdx}-${String(billIdx + 1).padStart(3, '0')}`;
        const meter = utility === 'ELECTRIC' ? 'ELECTRIC' : `${328175920 + locIdx * 1000 + utilIdx * 100 + billIdx}`;
        
        // Generate monthly statuses
        const monthlyStatuses: MonthlyStatus[] = months.map((month, monthIdx) => {
          // Randomly determine status (70% covered, 20% partial, 10% missing)
          const rand = Math.random();
          let status: 'covered' | 'partial' | 'missing';
          if (rand < 0.7) {
            status = 'covered';
          } else if (rand < 0.9) {
            status = 'partial';
          } else {
            status = 'missing';
          }
          
          // 80% chance of having an invoice
          const hasInvoice = Math.random() < 0.8;
          const billCount = status === 'covered' ? (hasInvoice ? 1 : 0) : 0;
          
          return {
            month: format(month, 'MMM yyyy'),
            status,
            hasInvoice,
            billCount,
          };
        });

        // Calculate %Last12Mo (percentage of months covered)
        const coveredMonths = monthlyStatuses.filter(m => m.status === 'covered').length;
        const percentLast12Mo = (coveredMonths / monthlyStatuses.length) * 100;

        return {
          location,
          account,
          meter,
          utility,
          billType,
          percentLast12Mo: parseFloat(percentLast12Mo.toFixed(2)),
          months: monthlyStatuses,
        };
      });
    });
  });
};

export function InvoiceParticipationHeatmap({
  dateFilter,
  utilityTypeFilter,
  billTypeFilter,
  locationFilter,
  vendorFilter,
  accountStatusFilter,
}: InvoiceParticipationHeatmapProps) {
  const [sortColumn, setSortColumn] = useState<string>('location');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const months = generateMonths();
  const dummyData = useMemo(() => generateDummyData(), []);

  // Filter data based on filters
  const filteredData = useMemo(() => {
    let filtered = [...dummyData];

    if (utilityTypeFilter !== 'all') {
      filtered = filtered.filter(row => 
        row.utility.toLowerCase() === utilityTypeFilter.toUpperCase()
      );
    }

    if (billTypeFilter !== 'all') {
      const billTypeMap: Record<string, string> = {
        'full-service': 'Full Service',
        'supply-only': 'Supply Only',
        'distribution-only': 'Distribution Only',
      };
      filtered = filtered.filter(row => 
        row.billType === billTypeMap[billTypeFilter]
      );
    }

    if (locationFilter !== 'all') {
      // Filter by location if needed
    }

    // Sort data
    filtered.sort((a, b) => {
      let aVal: any = a[sortColumn as keyof AccountRow];
      let bVal: any = b[sortColumn as keyof AccountRow];

      if (sortColumn === 'percentLast12Mo') {
        aVal = a.percentLast12Mo;
        bVal = b.percentLast12Mo;
      }

      if (typeof aVal === 'string') {
        return sortDirection === 'asc' 
          ? aVal.localeCompare(bVal as string)
          : (bVal as string).localeCompare(aVal);
      }

      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });

    return filtered;
  }, [dummyData, utilityTypeFilter, billTypeFilter, locationFilter, sortColumn, sortDirection]);

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getStatusColor = (status: 'covered' | 'partial' | 'missing') => {
    switch (status) {
      case 'covered':
        return 'bg-green-500 hover:bg-green-600';
      case 'partial':
        return 'bg-yellow-500 hover:bg-yellow-600';
      case 'missing':
        return 'bg-red-500 hover:bg-red-600';
      default:
        return 'bg-gray-200';
    }
  };

  return (
    <div className="space-y-4">
      {/* Legend and Instructions */}
      <Card className="border-l-4 border-l-blue-500">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Info className="h-5 w-5 text-blue-600" />
            <CardTitle className="text-lg">Invoice Participation Heatmap</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            This table shows if the service month is covered by one or more bills in the database, and how many invoices were received monthly.
          </p>
          <div className="flex items-center gap-6 flex-wrap">
            <div className="flex items-center gap-2">
              <div className="w-6 h-4 bg-green-500 rounded border"></div>
              <span className="text-sm">Service period is fully covered by at least 1 bill</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-4 bg-red-500 rounded border"></div>
              <span className="text-sm">Service period is partially/not covered</span>
            </div>
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-primary" />
              <span className="text-sm">Invoice received</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader className="bg-muted/30 sticky top-0 z-10">
                <TableRow>
                  <TableHead 
                    className="sticky left-0 bg-muted/30 z-20 min-w-[200px] cursor-pointer hover:bg-muted/50"
                    onClick={() => handleSort('location')}
                  >
                    <div className="flex items-center gap-2">
                      Location
                      {sortColumn === 'location' && (
                        <span className="text-xs">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </TableHead>
                  <TableHead className="min-w-[150px] cursor-pointer hover:bg-muted/50" onClick={() => handleSort('account')}>
                    <div className="flex items-center gap-2">
                      Account
                      {sortColumn === 'account' && (
                        <span className="text-xs">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </TableHead>
                  <TableHead className="min-w-[120px] cursor-pointer hover:bg-muted/50" onClick={() => handleSort('meter')}>
                    <div className="flex items-center gap-2">
                      Meter
                      {sortColumn === 'meter' && (
                        <span className="text-xs">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </TableHead>
                  <TableHead className="min-w-[120px] cursor-pointer hover:bg-muted/50" onClick={() => handleSort('utility')}>
                    <div className="flex items-center gap-2">
                      Utility
                      {sortColumn === 'utility' && (
                        <span className="text-xs">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </TableHead>
                  <TableHead className="min-w-[150px] cursor-pointer hover:bg-muted/50" onClick={() => handleSort('billType')}>
                    <div className="flex items-center gap-2">
                      Bill Type
                      {sortColumn === 'billType' && (
                        <span className="text-xs">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </TableHead>
                  <TableHead className="min-w-[120px] cursor-pointer hover:bg-muted/50" onClick={() => handleSort('percentLast12Mo')}>
                    <div className="flex items-center gap-2">
                      %Last12Mo
                      {sortColumn === 'percentLast12Mo' && (
                        <span className="text-xs">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </TableHead>
                  {months.map((month) => (
                    <TableHead 
                      key={format(month, 'MMM-yyyy')} 
                      className="min-w-[80px] text-center"
                    >
                      {format(month, 'MMM')}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredData.map((row, rowIdx) => (
                  <TableRow key={`${row.location}-${row.account}-${row.utility}-${row.billType}`}>
                    <TableCell className="sticky left-0 bg-background z-10 font-medium">
                      {row.location}
                    </TableCell>
                    <TableCell>{row.account}</TableCell>
                    <TableCell>{row.meter}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{row.utility}</Badge>
                    </TableCell>
                    <TableCell>{row.billType}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className={row.percentLast12Mo >= 80 ? 'text-green-600 font-semibold' : row.percentLast12Mo >= 50 ? 'text-yellow-600 font-semibold' : 'text-red-600 font-semibold'}>
                          {row.percentLast12Mo.toFixed(2)}%
                        </span>
                      </div>
                    </TableCell>
                    {row.months.map((monthStatus, monthIdx) => (
                      <TableCell key={monthIdx} className="p-1">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="relative w-full h-8 flex items-center justify-center">
                                <div
                                  className={`w-full h-6 rounded border ${getStatusColor(monthStatus.status)} transition-colors cursor-pointer flex items-center justify-center`}
                                >
                                  {monthStatus.hasInvoice && (
                                    <FileText className="h-3 w-3 text-white" />
                                  )}
                                </div>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              <div className="text-sm">
                                <p className="font-semibold">{monthStatus.month}</p>
                                <p>Status: {monthStatus.status === 'covered' ? 'Fully Covered' : monthStatus.status === 'partial' ? 'Partially Covered' : 'Missing'}</p>
                                {monthStatus.hasInvoice && (
                                  <p>Invoice: Received</p>
                                )}
                                {monthStatus.billCount > 0 && (
                                  <p>Bills: {monthStatus.billCount}</p>
                                )}
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

